import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import { allRoutes } from '../../lib/constants/route';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { historySVG, plusSignSVG, questionSVG } from '../../lib/allCustomSVGs';
import Popup from '../../ui/popup/Popup';
import { get_presentation_frequencies_api_call } from '../../lib/api/presentations/presentations-api-call';
import ReportsPresentations from './ReportsPresentations/ReportsPresentations';
import ReportsCustomPrompts from './ReportsCustomPrompts/ReportsCustomPrompts';
import AddReportsPresentations from './ReportsPresentations/AddReportsPresentations';
import EditReportsPresentations from './ReportsPresentations/EditReportsPresentations';
import AddReportsCustomPrompts from './ReportsCustomPrompts/AddReportsCustomPrompts';
import EditReportsCustomPrompts from './ReportsCustomPrompts/EditReportsCustomPrompts';
import ReportsGraphs from './ReportsGraphs/ReportsGraphs';
import AddReportsGraphs from './ReportsGraphs/AddReportsGraphs';
import EditReportsGraphs from './ReportsGraphs/EditReportsGraphs';
import ReportsNotifications from './ReportsNotifications/ReportsNotifications';
import AddReportsNotifications from './ReportsNotifications/AddReportsNotifications';

import { GraphApi } from '../../lib/api/Reports/report_graph_api_call';
import { CustomPrompt } from '../../interfaces/CustomPrompt';
import HELP_LINKS from '../../lib/constants/helpLinks';
import PresentationExecutionHistory from './ReportsPresentations/PresentationExecutionHistory';
import NotificationsHistory from './ReportsNotifications/NotificationsHistory';

type LoadState = 'pending' | 'success' | 'error' | 'null';
type BtnType =
  | 'danger'
  | 'warning'
  | 'success'
  | 'light'
  | 'filter'
  | 'delete'
  | 'white'
  | undefined;

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [integrationsAndMetrics, setIntegrationsAndMetrics] = useState<any[]>(
    []
  );
  const [graphsForAdd, setGraphsForAdd] = useState<GraphApi[]>([]);
  const [promptsForAdd, setPromptsForAdd] = useState<CustomPrompt[]>([]);
  const [addNotificationsReady, setAddNotificationsReady] = useState(false);

  const [isFrequenciesLoading, setIsFrequenciesLoading] =
    useState<LoadState>('pending');
  const [presentationFrequencies, setPresentationFrequencies] = useState<{
    [key: string]: string;
  } | null>(null);
  const [graphFrequencies, setGraphFrequencies] = useState<{
    [key: string]: string;
  } | null>(null);

  const [isAddPresentationPopupOpen, setIsAddPresentationPopupOpen] =
    useState(false);
  const [isEditPresentationPopupOpen, setIsEditPresentationPopupOpen] =
    useState(false);
  const [editingPresentation, setEditingPresentation] = useState<any>(null);

  const [isAddNotificationPopupOpen, setIsAddNotificationPopupOpen] =
    useState(false);
  const [isAddPromptPopupOpen, setIsAddPromptPopupOpen] = useState(false);
  const [isEditPromptPopupOpen, setIsEditPromptPopupOpen] = useState(false);
  const [editingCustomPrompt, setEditingCustomPrompt] = useState<any>(null);
  const [isEditGraphPopupOpen, setIsEditGraphPopupOpen] = useState(false);
  const [isPresentationsHistoryPopupOpen, setIsPresentationsHistoryPopupOpen] =
    useState(false);
  const [isNotificationsHistoryPopupOpen, setIsNotificationsHistoryPopupOpen] =
    useState(false);
  const [editingGraph, setEditingGraph] = useState<any>(null);
  const [isAddGraphPopupOpen, setIsAddGraphPopupOpen] = useState(false);

  const [presentationsRefreshTrigger, setPresentationsRefreshTrigger] =
    useState(0);
  const [customPromptsRefreshTrigger, setCustomPromptsRefreshTrigger] =
    useState(0);
  const [graphsRefreshTrigger, setGraphsRefreshTrigger] = useState(0);
  const [notificationsRefreshTrigger, setNotificationsRefreshTrigger] =
    useState(0);

  useEffect(() => {
    if (location.pathname === '/reports') {
      navigate('/reports/notifications', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Fetch frequencies
  useEffect(() => {
    const fetchFrequencies = async () => {
      setIsFrequenciesLoading('pending');
      try {
        const [frequencies] = await Promise.all([
          get_presentation_frequencies_api_call(),
        ]);
        if (frequencies) {
          setPresentationFrequencies(frequencies);
          setGraphFrequencies(frequencies);
          setIsFrequenciesLoading('success');
        } else {
          setIsFrequenciesLoading('null');
        }
      } catch (err) {
        console.error('Error fetching settings/frequencies:', err);
        setIsFrequenciesLoading('error');
      }
    };
    fetchFrequencies();
  }, []);

  const toggleAddPromptPopupModal = useCallback(
    () => setIsAddPromptPopupOpen((s) => !s),
    []
  );
  const toggleEditPromptPopupModal = useCallback(
    () => setIsEditPromptPopupOpen((s) => !s),
    []
  );
  const toggleAddPresentationPopupModal = useCallback(
    () => setIsAddPresentationPopupOpen((s) => !s),
    []
  );
  const toggleEditPresentationPopupModal = useCallback(
    () => setIsEditPresentationPopupOpen((s) => !s),
    []
  );
  const toggleAddGraphPopupModal = useCallback(
    () => setIsAddGraphPopupOpen((s) => !s),
    []
  );
  const toggleEditGraphPopupModal = useCallback(
    () => setIsEditGraphPopupOpen((s) => !s),
    []
  );
  const togglePresentationsHistoryPopupModal = useCallback(
    () => setIsPresentationsHistoryPopupOpen((s) => !s),
    []
  );
  const toggleNotificationsHistoryPopupModal = useCallback(
    () => setIsNotificationsHistoryPopupOpen((s) => !s),
    []
  );
  const toggleAddNotificationPopupModal = useCallback(
    () => setIsAddNotificationPopupOpen((s) => !s),
    []
  );

  // Handlers
  const handleAddPresentation = useCallback(() => {
    toggleAddPresentationPopupModal();
  }, [toggleAddPresentationPopupModal]);

  const handlePresentationAdded = useCallback(() => {
    setPresentationsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handlePresentationUpdated = useCallback(() => {
    setPresentationsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleEditPresentation = useCallback((presentation: any) => {
    setEditingPresentation(presentation);
    setIsEditPresentationPopupOpen(true);
  }, []);

  const handleAddPrompt = useCallback(() => {
    toggleAddPromptPopupModal();
  }, [toggleAddPromptPopupModal]);

  const handleCustomPromptAdded = useCallback(() => {
    setCustomPromptsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleCustomPromptUpdated = useCallback(() => {
    setCustomPromptsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleEditCustomPrompt = useCallback((customPrompt: any) => {
    setEditingCustomPrompt(customPrompt);
    setIsEditPromptPopupOpen(true);
  }, []);

  const handleAddGraph = useCallback(() => {
    toggleAddGraphPopupModal();
  }, [toggleAddGraphPopupModal]);

  const handleEditGraph = useCallback((graph: any) => {
    setEditingGraph(graph);
    setIsEditGraphPopupOpen(true);
  }, []);

  const handleGraphAdded = useCallback(() => {
    setGraphsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleGraphUpdated = useCallback(() => {
    setGraphsRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleAddNotification = useCallback(() => {
    toggleAddNotificationPopupModal();
  }, [toggleAddNotificationPopupModal]);

  const handleNotificationAdded = useCallback(() => {
    setNotificationsRefreshTrigger((prev) => prev + 1);
  }, []);

  // Top-right button config
  const {
    subTitle,
    btnTitle,
    btnIcon,
    btnType,
    btnAction,
    btnLoading,
    btnDisabled,
    helpBtnLink,
    historyBtnTitle,
    historyBtnAction,
  } = useMemo(() => {
    const path = location.pathname;

    if (path.includes('/reports/presentations')) {
      return {
        subTitle: t('ManageYourPresentationAutomations'),
        btnTitle: t('AddNew'),
        btnIcon: plusSignSVG,
        btnType: undefined as BtnType,
        btnAction: handleAddPresentation,
        btnLoading: false,
        btnDisabled:
          !presentationFrequencies || isFrequenciesLoading !== 'success',

        helpBtnLink: HELP_LINKS.reports.presentations,
        historyBtnTitle: t('History'),
        historyBtnAction: togglePresentationsHistoryPopupModal,
      };
    }

    if (path.includes('/reports/prompts')) {
      return {
        subTitle: t('CreateAndManageCustomAIPrompts'),
        btnTitle: t('AddNew'),
        btnIcon: plusSignSVG,
        btnType: undefined as BtnType,
        btnAction: handleAddPrompt,
        btnLoading: false,
        btnDisabled: false,

        helpBtnLink: HELP_LINKS.reports.prompts,
        historyBtnTitle: null,
        historyBtnAction: () => {},
      };
    }

    if (path.includes('/reports/graphs')) {
      return {
        subTitle: t('CreateAndManageCustomGraphs'),
        btnTitle: t('AddNew'),
        btnIcon: plusSignSVG,
        btnType: undefined as BtnType,
        btnAction: handleAddGraph,
        btnLoading: false,
        btnDisabled: !graphFrequencies,

        helpBtnLink: HELP_LINKS.reports.graphs,
        historyBtnTitle: null,
        historyBtnAction: () => {},
      };
    }

    // Notifications route
    return {
      subTitle: t('CreateAndManageNotifications'),
      btnTitle: t('AddNew'),
      btnIcon: plusSignSVG,
      btnType: undefined as BtnType,
      btnAction: handleAddNotification,
      btnLoading: false,
      // disable until ReportsNotifications preloads integrations, graphs, prompts
      btnDisabled: !addNotificationsReady,

      helpBtnLink: HELP_LINKS.reports.notifications,
      historyBtnTitle: t('History'),
      historyBtnAction: toggleNotificationsHistoryPopupModal,
    };
  }, [
    location.pathname,
    t,
    handleAddPresentation,
    handleAddPrompt,
    handleAddGraph,
    handleAddNotification,
    presentationFrequencies,
    graphFrequencies,
    isFrequenciesLoading,
    addNotificationsReady,
    togglePresentationsHistoryPopupModal,
    toggleNotificationsHistoryPopupModal,
  ]);

  return (
    <PageWrapperMain>
      <SubPageContentHolder>
        <SubPageInnerContentHolder
          title={t('Reports')}
          subTitle={subTitle ?? ''}
          /* ------Add New Button------ */
          secondaryButtonType={btnType}
          secondaryBtnTitle={btnTitle}
          secondaryBtnIcon={btnIcon}
          secondaryBtnLoading={btnLoading}
          secondaryBtnDisabled={btnDisabled}
          secondaryBtnAction={btnAction}
          /* ------Help Button------ */
          mainButtonType='white'
          mainBtnTitle={t('HelpMeSetup')}
          mainBtnIcon={questionSVG}
          mainBtnLoading={false}
          mainBtnLink={helpBtnLink}
          isMainBtnLinkExternal={true}
          /* ------History Button------ */
          tertiaryBtnTitle={historyBtnTitle}
          tertiaryBtnIcon={historySVG}
          tertiaryBtnLoading={false}
          tertiaryBtnDisabled={false}
          tertiaryBtnAction={historyBtnAction}
          tertiaryButtonType='white'
        >
          {/* Add Notifications */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleAddNotificationPopupModal}
            trigger={isAddNotificationPopupOpen}
          >
            <AddReportsNotifications
              togglePopupModal={toggleAddNotificationPopupModal}
              onNotificationAdded={handleNotificationAdded}
              integrationsAndMetrics={integrationsAndMetrics}
              // Preloaded lists
              graphs={graphsForAdd}
              prompts={promptsForAdd}
            />
          </Popup>

          {/* Presentations */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleAddPresentationPopupModal}
            trigger={isAddPresentationPopupOpen}
          >
            <AddReportsPresentations
              togglePopupModal={toggleAddPresentationPopupModal}
              onPresentationAdded={handlePresentationAdded}
              frequencies={presentationFrequencies}
            />
          </Popup>
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleEditPresentationPopupModal}
            trigger={isEditPresentationPopupOpen}
          >
            {editingPresentation && (
              <EditReportsPresentations
                togglePopupModal={toggleEditPresentationPopupModal}
                onPresentationUpdated={handlePresentationUpdated}
                frequencies={presentationFrequencies}
                presentation={editingPresentation}
              />
            )}
          </Popup>

          {/* Prompts */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleAddPromptPopupModal}
            trigger={isAddPromptPopupOpen}
          >
            <AddReportsCustomPrompts
              togglePopupModal={toggleAddPromptPopupModal}
              onCustomPromptAdded={handleCustomPromptAdded}
            />
          </Popup>
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleEditPromptPopupModal}
            trigger={isEditPromptPopupOpen}
          >
            {editingCustomPrompt && (
              <EditReportsCustomPrompts
                togglePopupModal={toggleEditPromptPopupModal}
                onCustomPromptUpdated={handleCustomPromptUpdated}
                customPrompt={editingCustomPrompt}
              />
            )}
          </Popup>

          {/* Graphs */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleAddGraphPopupModal}
            trigger={isAddGraphPopupOpen}
          >
            <AddReportsGraphs
              togglePopupModal={toggleAddGraphPopupModal}
              onGraphAdded={handleGraphAdded}
              integrationsAndMetrics={integrationsAndMetrics}
            />
          </Popup>
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleEditGraphPopupModal}
            trigger={isEditGraphPopupOpen}
          >
            {editingGraph && (
              <EditReportsGraphs
                togglePopupModal={toggleEditGraphPopupModal}
                onGraphUpdated={handleGraphUpdated}
                graph={editingGraph}
                integrationsAndMetrics={integrationsAndMetrics}
              />
            )}
          </Popup>

          {/* Presentations Execution History Popup */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={togglePresentationsHistoryPopupModal}
            trigger={isPresentationsHistoryPopupOpen}
            additionalClassName='presentationExecutionHistoryModel'
          >
            <PresentationExecutionHistory
              togglePopupModal={togglePresentationsHistoryPopupModal}
            />
          </Popup>

          {/* Notifications Execution History Popup */}
          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleNotificationsHistoryPopupModal}
            trigger={isNotificationsHistoryPopupOpen}
            additionalClassName='presentationExecutionHistoryModel'
          >
            <NotificationsHistory
              togglePopupModal={toggleNotificationsHistoryPopupModal}
            />
          </Popup>

          <Routes>
            {allRoutes['/reports/notifications'] && (
              <Route
                path='notifications/*'
                element={
                  <ReportsNotifications
                    refreshTrigger={notificationsRefreshTrigger}
                    setIntegrationsAndMetrics={setIntegrationsAndMetrics}
                    setAddNotificationsReady={setAddNotificationsReady}
                    setGraphsForAdd={setGraphsForAdd}
                    setPromptsForAdd={setPromptsForAdd}
                  />
                }
              />
            )}

            {allRoutes['/reports/presentations'] && (
              <Route
                path='presentations/*'
                element={
                  <ReportsPresentations
                    refreshTrigger={presentationsRefreshTrigger}
                    frequencies={presentationFrequencies}
                    onEditPresentation={handleEditPresentation}
                  />
                }
              />
            )}

            {allRoutes['/reports/prompts'] && (
              <Route
                path='prompts/*'
                element={
                  <ReportsCustomPrompts
                    refreshTrigger={customPromptsRefreshTrigger}
                    onEditCustomPrompt={handleEditCustomPrompt}
                  />
                }
              />
            )}

            {allRoutes['/reports/graphs'] && (
              <Route
                path='graphs/*'
                element={
                  <ReportsGraphs
                    refreshTrigger={graphsRefreshTrigger}
                    onEditGraph={handleEditGraph}
                    setIntegrationsAndMetrics={setIntegrationsAndMetrics}
                  />
                }
              />
            )}
          </Routes>
        </SubPageInnerContentHolder>
      </SubPageContentHolder>
    </PageWrapperMain>
  );
};

export default Notifications;
